import { html, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import unsaferHTML from "@escapio/shared/unsaferHTML";
import { debounce } from "../../shared/event/debounce";
import { translate } from "../../shared/l10n/translate";
import { data } from "../../shared/state/payload";
import { get as getHistory } from "../state/history";
import {
	addEventTrackerData,
	addMetricTrackerData,
} from "../tracker/EventQueue";
import { track, trackImpression } from "../tracker/EventTracker";
import { send as sendMetric } from "../tracker/Metric";
import { stateful } from "@escapio/shared/components/element-builder";

const TRACK_CATEGORY = "Suche";
export const TYPE_ALL_RESULTS = "all-results";

const DROPDOWN = Object.freeze({
	HIDDEN: Symbol("HIDDEN"),
	SUGGESTIONS: Symbol("SUGGESTIONS"),
	HISTORY: Symbol("HISTORY"),
	SEARCH_INSPIRATION: Symbol("INSPIRATION"),
});

const SUGGESTION_LIMIT = 5;

/**
 * These events are imported, so they have to fulfil a specific format.
 * @see comment: "import-search-results"
 */
const formatSelectedEntryEventLabel = (
	position,
	type,
	itemId,
	origin,
	suffix = "",
) => `${position} | ${type} | ${itemId} | Dropdown | ${origin}${suffix}`;

const getCountLabel = num =>
	num === 1
		? translate("basic_hotel_singular")
		: translate("basic_hotel_plural");

const escapeHTML = input => new Option(input).innerHTML;

customElements.define(
	"esc-search-input",
	stateful(
		class extends HTMLElement {
			static getInitialState() {
				return {
					value: "",
					_dropDownStatus: DROPDOWN.HIDDEN,
					isLoading: false,
					entries: [],
					selected: 0,
					requestTracked: false,
					arrowsTracked: false,
					lastSearch: null,
				};
			}

			constructor() {
				super();
				this.makeRequest = debounce(400, this.makeRequest.bind(this));
			}

			connectedCallback() {
				this.addEventListener("focusout", e => {
					if (!this.contains(e.relatedTarget)) {
						/*
						 * Chrome scrolls inputs with some value into view when they lose focus.
						 * This scrolling happens before the click event is triggered,
						 * so clicks on links which are to far away from the input won't be processed.
						 *
						 * As a workaround we will only close the dropdown
						 * if the current focus is outside of the search input custom element.
						 */
						setTimeout(() => {
							/*
							 * ESC-11034: fix-ignored-dropdown-clicks
							 * Safari sometimes runs this too fast so that actual clicks
							 * on links in the dropdown are ignored.
							 */
							this.onBlur();
						}, 100);
					}
				});

				/**
				 * Hack: SAFARI-BACK-NAV-INPUT-RESET
				 *
				 * Safari seems to clear inputs with autocomplete=off on back navigation.
				 * This happens some time after the "pageshow" event.
				 */
				window.addEventListener("pageshow", ({ persisted }) => {
					if (persisted) {
						setTimeout(() => {
							this.querySelector("input").value = this.value;
						}, 300);
					}
				});
			}

			get dropDownStatus() {
				return this._dropDownStatus;
			}

			set dropDownStatus(dropDownStatus) {
				if (this._dropDownStatus === dropDownStatus) {
					return;
				}

				if (this._dropDownStatus === DROPDOWN.HIDDEN) {
					this.dispatchEvent(new CustomEvent("open"));
				}
				this._dropDownStatus = dropDownStatus;
			}

			render() {
				render(
					html`
						<input
							type="search"
							name="q"
							placeholder=${translate("search_panel_term_placeholder")}
							aria-label=${translate("search_panel_term_placeholder")}
							autocomplete="off"
							.value=${this.value}
							@input=${this.onInputChange}
							@focus=${this.onInputFocus}
							@keydown=${this.onInputKeydown}
						/>

						<span class="input-with-icon--addon"
							><i class="fa fa-search"></i>
						</span>

						<button
							type="button"
							class="search-panel-term__clear-button ${classMap({
								"search-panel-term__clear-button--show": this.value,
							})}"
							aria-label=${translate("basic_delete")}
							@click=${this.onClearClick}
						>
							<span class="close-icon close-icon--with-hover"></span>
						</button>

						<div
							class="search-field-suggest__loader-node ${classMap({
								"search-field-suggest__loader-node--show": this.isLoading,
							})}"
						>
							<span>
								<i
									class="fa fa-circle animated-trio-loader-element ${classMap({
										"animation-off": !this.isLoading,
									})}"
								></i>
								<i
									class="fa fa-circle animated-trio-loader-element ${classMap({
										"animation-off": !this.isLoading,
									})}"
								></i>
								<i
									class="fa fa-circle animated-trio-loader-element ${classMap({
										"animation-off": !this.isLoading,
									})}"
								></i>
							</span>
						</div>

						<div
							class="search-field-suggest ${classMap({
								"search-field-suggest--show":
									this.dropDownStatus !== DROPDOWN.HIDDEN,
							})}"
						>
							${this.dropDownStatus === DROPDOWN.HISTORY
								? html`
										<div class="search-field-suggest__divider">
											${translate("search_field_suggests_recently")}
										</div>
									`
								: null}
							${this.dropDownStatus === DROPDOWN.SEARCH_INSPIRATION
								? html`
										<div class="search-field-suggest__divider">
											${translate("search_field_suggests_inspiration")}
										</div>
									`
								: null}
							${this.entries.length === 0 && !this.isLoading
								? html`
										<div class="search-field-suggest__no-result ellipsis">
											${unsaferHTML(
												translate(
													"search_field_suggests_no_result",
													`<strong>${escapeHTML(this.value)}</strong>`,
												),
											)}
										</div>
									`
								: null}
							${this.entries.map(
								(entry, index) =>
									html` <a
										href=${entry.link}
										class="search-field-suggest__link ${classMap({
											"search-field-suggest__link--highlighted":
												index === this.selected,
											"search-field-suggest__all-results-link":
												entry.type === TYPE_ALL_RESULTS,
										})}"
										@mouseenter=${this.getEnterHandler(index)}
										@click=${evt => this.lockEntry(evt, index, "Klick")}
									>
										${entry.type === TYPE_ALL_RESULTS
											? html`
													<div
														class="search-field-suggest__type-icon-search fa fa-search"
													></div>
													<div
														class="search-field-suggest__all-results ellipsis"
													>
														${unsaferHTML(
															translate(
																"search_field_suggests_all_results",
																`<strong>${escapeHTML(this.value)}</strong>`,
															),
														)}
													</div>
												`
											: html` <div
														class="search-field-suggest__type-icon search-field-suggest__type-icon-${entry.type}"
													></div>
													<div class="search-field-suggest__infos ellipsis">
														<div
															class="search-field-suggest__name-and-hotels-number"
														>
															<span class="search-field-suggest__name ellipsis"
																>${unsaferHTML(entry.label)}</span
															>
															${entry.hotels_number
																? html`
																		<span
																			class="search-field-suggest__hotels-number"
																		>
																			(${entry.hotels_number}
																			${getCountLabel(entry.hotels_number)})
																		</span>
																	`
																: null}
														</div>

														<div
															class="search-field-suggest__type-and-geo-info ellipsis"
														>
															<span class="search-field-suggest__type-label">
																${entry.type === "combi"
																	? html`${translate("basic_hotel_type")}
																		&ndash;`
																	: null}
																${entry.type_label}
															</span>
															${entry.geo_paths && entry.geo_paths.length > 0
																? html`
																		<span
																			class="search-field-suggest__geo-path-icon"
																			>&ndash;</span
																		>
																		${entry.geo_paths.map(
																			(geoPath, index) => html`
																				${index > 0
																					? html`<span
																							class="fa fa-angle-right search-field-suggest__geo-path-icon"
																						></span>`
																					: null}
																				<span
																					class="search-field-suggest__geo-path"
																					>${unsaferHTML(geoPath)}</span
																				>
																			`,
																		)}
																	`
																: null}
														</div>
													</div>`}
									</a>`,
							)}
						</div>
					`,
					this,
					{ host: this },
				);
			}

			get searchInspiration() {
				return JSON.parse(this.getAttribute("search-inspiration"));
			}

			async makeRequest() {
				if (this.abortController) {
					this.abortController.abort();
					this.abortController = null;
				}

				if (this.value === "") {
					this.isLoading = false;
					this.showHistoryOrInspiration();
					return;
				}

				if (!this.requestTracked) {
					this.requestTracked = true;
					sendMetric("search-count", 1);
				}

				const url = new URL(data.uris.suggest);
				url.searchParams.set("q", this.value);
				url.searchParams.set("l", SUGGESTION_LIMIT);

				this.selected = 0;
				this.isLoading = true;
				this.dropDownStatus = DROPDOWN.HIDDEN;
				this.entries = [];

				this.abortController = new AbortController();

				const response = await fetch(url, {
					signal: this.abortController.signal,
				});
				const { results, total } = await response.json();

				this.abortController = null;
				this.entries = results;
				this.isLoading = false;
				this.dropDownStatus = DROPDOWN.SUGGESTIONS;

				if (total > results.length) {
					const link = new URL(data.uris.search);
					link.searchParams.set("q", this.value);
					this.entries = [
						...this.entries,
						{ type: TYPE_ALL_RESULTS, link, label: this.value },
					];
				}

				if (results.length === 0) {
					if (
						this.lastSearch === null ||
						this.value.startsWith(this.lastSearch)
					) {
						this.lastSearch = this.value;
						sendMetric("search-no-results-count", 1);
						trackImpression(
							TRACK_CATEGORY,
							this.value.toLowerCase(),
							"0 |  Dropdown",
						);
					}
					sendMetric("search-no-results-query-length", this.value.length);
				}
			}

			showHistoryOrInspiration() {
				this.selected = 0;
				const filterCurrent = ({ link }) => link !== window.location.pathname;
				const map = {
					[DROPDOWN.SEARCH_INSPIRATION]: this.searchInspiration,
					[DROPDOWN.HISTORY]: getHistory(),
				};

				const [state, entries] = Object.getOwnPropertySymbols(map).reduce(
					(prev, key) => {
						const filtered = map[key].filter(filterCurrent);
						return filtered.length ? [key, filtered] : prev;
					},
					[DROPDOWN.HIDDEN, []],
				);
				this.dropDownStatus = state;
				this.entries = entries.slice(0, SUGGESTION_LIMIT);

				if (this.dropDownStatus === DROPDOWN.HISTORY) {
					track(
						TRACK_CATEGORY,
						"Zuletzt gesehen angezeigt",
						this.entries.length,
						null,
					);
				}
				if (this.dropDownStatus === DROPDOWN.SEARCH_INSPIRATION) {
					track(
						TRACK_CATEGORY,
						"Meist gesucht angezeigt",
						this.entries.map(entry => entry["item_id"]).join(","),
					);
				}
			}

			onInputChange(event) {
				this.isLoading = true;
				this.dropDownStatus = DROPDOWN.HIDDEN;
				this.value = event.target.value;
				this.makeRequest();
			}

			onInputFocus() {
				this.showHistoryOrInspiration();
			}

			onInputKeydown(event) {
				const deltaMap = { ArrowUp: -1, ArrowDown: +1 };

				switch (event.key) {
					case "ArrowUp":
					case "ArrowDown":
						event.preventDefault();
						if (!this.arrowsTracked) {
							this.arrowsTracked = true;
							track(
								TRACK_CATEGORY,
								this.value.toLowerCase(),
								"Dropdown | Pfeiltasten genutzt",
								null,
							);
						}
						this.selected = Math.min(
							Math.max(0, this.selected + deltaMap[event.key]),
							this.entries.length - 1,
						);
						break;

					case "Escape":
						event.preventDefault();
						this.onBlur();
						break;

					case "Enter":
						this.lockEntry(event, this.selected, "Enter");
						break;
				}
			}

			onBlur() {
				this.dropDownStatus = DROPDOWN.HIDDEN;
			}

			onClearClick() {
				track(
					TRACK_CATEGORY,
					"Klick Suchbegriff entfernt",
					this.value.toLowerCase(),
				);
				this.value = "";
				this.querySelector("input").focus();
			}

			getEnterHandler(index) {
				return () => {
					this.selected = index;
				};
			}

			lockEntry(evt, index, origin) {
				evt.preventDefault();
				if (this.isLoading || !this.entries[index]) {
					return;
				}
				const lockedEntry = this.entries[index];
				this.trackVisit(lockedEntry, index, origin);
				this.dispatchEvent(
					new CustomEvent("lockEntry", { detail: lockedEntry }),
				);
				this.dropDownStatus = DROPDOWN.HIDDEN;
			}

			trackVisit(entry, index, origin) {
				if (entry.type === TYPE_ALL_RESULTS) {
					addEventTrackerData(
						TRACK_CATEGORY,
						this.value.toLowerCase(),
						`${this.entries.length} | Dropdown | weitere Ergebnisse | ${origin}`,
						null,
					);
					addMetricTrackerData("search-click-more-results-count", 1);
					addMetricTrackerData(
						"search-click-more-results-dropdown-query-length",
						this.value.length,
					);
				} else if (this.dropDownStatus === DROPDOWN.HISTORY) {
					addEventTrackerData(
						TRACK_CATEGORY,
						`Zuletzt gesehen geöffnet`,
						`${entry.type}: ${entry["item_id"]} | ${index + 1} | ${
							this.entries.length
						}`,
						null,
					);
				} else if (this.dropDownStatus === DROPDOWN.SEARCH_INSPIRATION) {
					addEventTrackerData(
						TRACK_CATEGORY,
						`Meist gesucht geöffnet`,
						formatSelectedEntryEventLabel(
							index + 1,
							entry.type,
							entry["item_id"],
							origin,
						),
					);
				} else {
					addEventTrackerData(
						TRACK_CATEGORY,
						this.value.toLowerCase(),
						formatSelectedEntryEventLabel(
							index + 1,
							entry.type,
							entry["item_id"],
							origin,
							entry["is_alternative"] ? " (alternativ)" : "",
						),
						null,
					);
					addMetricTrackerData("search-click-result-position", index + 1);
					addMetricTrackerData(
						"search-click-result-query-length",
						this.value.length,
					);
				}
			}
		},
	),
);
